/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import isNil from 'lodash/isNil';

import ErrorName from '../../error/error-name';
import PromiseUtil from '../../promise/promise-util';

/**
 * @import { ContentInfo } from '../../content/info/content-info'
 * @import { Logger } from '../../logger/logger'
 * @import ScreenManager from '../../screen/screen-manager'
 */

/**
 * @typedef {Pick<ContentInfo, 'backlight' | 'orientationEnum'>} IContentInfo
 */

/**
 * Nahrazovač nastavení obrazovky zařízení.
 *
 * Stará se o aktualizaci orientace zařízení dle obsahu datového skladu.
 */
export default class ReplacerScreen {
  /**
   * Vytvoří instanci.
   * @param {ScreenManager} screenManager
   * @param {Logger} logger
   */
  constructor(screenManager, logger) {
    this.screenManager = screenManager;
    this.logger = logger;
    this.handleError = this.handleError.bind(this);
  }

  /**
   * @private
   * @param {Error} error
   * @returns {void}
   */
  handleError(error) {
    // Chyby vycházející z nepodpory operací se ignorují.
    if (error.name === ErrorName.NotSupportedError) {
      return this.logger.warn(error);
    }
    throw error;
  }

  /**
   * Změní nastavení obrazovky dle obsahu.
   * @param {IContentInfo} contentInfo
   * @returns {Promise<void>}
   */
  async replace(contentInfo) {
    const { backlight, orientationEnum } = contentInfo;

    await PromiseUtil.allSerial([
      () => this.replaceBacklight(backlight),
      () => this.replaceOrientation(orientationEnum)
    ]).catch(this.handleError);
  }

  /**
   * @private
   * @param {ContentInfo['backlight']} backlight
   */
  async replaceBacklight(backlight) {
    if (!isNil(backlight)) {
      await this.screenManager.setBacklight(backlight);
    }
  }

  /**
   * @private
   * @param {ContentInfo['orientationEnum']} orientationEnum
   */
  async replaceOrientation(orientationEnum) {
    if (!isNil(orientationEnum)) {
      await this.screenManager.setOrientation(orientationEnum);
    }
  }
}
